import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { BookCTA } from '../components/PageShell';
import { FaqSection } from '../components/SEO';
import { ORGANIZATION_LD } from '../components/SEO';
import { PageHero } from '../components/PageShell';
import { PageShell } from '../components/PageShell';
import { SEO } from '../components/SEO';
import { SITE_URL } from '../lib/constants';
import { buildFaqLd } from '../components/SEO';
import { useLanguage } from '../context/LanguageContext';

const CourtsPage = () => {
  const { t } = useLanguage();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const seo = t.seo.courts;
  const faqs = t.courtsPage.faqs || [];
  const heroImg = 'https://customer-assets-rejwkqb3.emergentagent.net/job_padel-arena-modern/artifacts/gifcfzdk_DSC06849.webp';
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'SportsActivityLocation',
      '@id': `${SITE_URL}/courts#courts`,
      name: '8 Indoor Padel Courts – Padelarena Wädenswil',
      url: `${SITE_URL}/courts`,
      image: heroImg,
      description: seo.description,
      sport: 'Padel',
      address: ORGANIZATION_LD.address,
      telephone: '+41 43 833 0834',
      amenityFeature: [
        { '@type': 'LocationFeatureSpecification', name: 'Indoor courts', value: 8 },
        { '@type': 'LocationFeatureSpecification', name: 'LED lighting' },
        { '@type': 'LocationFeatureSpecification', name: 'Racket rental' },
        { '@type': 'LocationFeatureSpecification', name: 'Locker rooms with showers' }
      ],
      potentialAction: {
        '@type': 'ReserveAction',
        target: 'https://bookpadelarena.ch',
        result: { '@type': 'Reservation', name: 'Padel court booking' }
      },
      parentOrganization: { '@id': `${SITE_URL}/#organization` }
    },
    faqs.length ? buildFaqLd(faqs) : null
  ].filter(Boolean);

  return (
    <PageShell testId="courts-page">
      <SEO
        path="/courts"
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        image={heroImg}
        jsonLd={jsonLd}
      />
      <PageHero
        label={t.courtsPage.label}
        title={t.courtsPage.titleStart}
        accent={t.courtsPage.titleAccent}
        subtitle={t.courtsPage.subtitle}
        image={heroImg}
      />
      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12 space-y-10">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">{t.courtsPage.aboutTitle}</h2>
            <p className="text-slate-700 leading-relaxed mb-4">{t.courtsPage.about1}</p>
            <p className="text-slate-700 leading-relaxed">{t.courtsPage.about2}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { v: '8', l: t.courtsPage.statCourts },
              { v: '100%', l: t.courtsPage.statIndoor },
              { v: '5 CHF', l: t.courtsPage.statRacket },
              { v: '60–120', l: t.courtsPage.statSlot }
            ].map((s, i) => (
              <div key={i} className="bg-slate-50 rounded-2xl p-4 text-center border border-slate-100">
                <p className="text-3xl font-black text-slate-900">{s.v}</p>
                <p className="text-xs text-slate-500 mt-1 uppercase tracking-wider">{s.l}</p>
              </div>
            ))}
          </div>

          {/* Opening hours panel */}
          <div className="rounded-2xl p-6 bg-slate-900 text-white" data-testid="courts-opening-hours">
            <p className="text-xs font-bold uppercase tracking-widest text-sky-400 mb-3">{t.courtsPage.hoursTitle}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                <span className="text-sm text-slate-300">{t.courtsPage.hoursWeekday}</span>
                <span className="text-base font-bold">06:00 – 24:00</span>
              </div>
              <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                <span className="text-sm text-slate-300">{t.courtsPage.hoursWeekend}</span>
                <span className="text-base font-bold">08:00 – 20:00</span>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-xl font-bold text-slate-900 mb-4">{t.courtsPage.includedTitle}</h3>
            <ul className="space-y-2 text-slate-700">
              {t.courtsPage.included.map((it, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-sky-500 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Court photo gallery */}
          <div data-testid="courts-gallery">
            <h3 className="text-xl font-bold text-slate-900 mb-4">{t.courtsPage.galleryTitle}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:row-span-2 aspect-[4/5] md:aspect-auto rounded-2xl overflow-hidden border border-slate-100">
                <img src="https://customer-assets-rejwkqb3.emergentagent.net/job_padel-arena-modern/artifacts/aqs0h1ck_DSC06840.webp" alt="Indoor padel courts at Padelarena Wädenswil" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="aspect-[16/10] rounded-2xl overflow-hidden border border-slate-100">
                <img src="https://customer-assets-rejwkqb3.emergentagent.net/job_padel-arena-modern/artifacts/xwo6ovhs_DSC06850.webp" alt="Padel court view at Padelarena" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="aspect-[16/10] rounded-2xl overflow-hidden border border-slate-100">
                <img src="https://customer-assets-rejwkqb3.emergentagent.net/job_padel-arena-modern/artifacts/nj8h18ke_DSC06837.webp" alt="Padel match at Padelarena" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="md:col-span-2 aspect-[21/9] rounded-2xl overflow-hidden border border-slate-100">
                <img src="https://customer-assets-rejwkqb3.emergentagent.net/job_padel-arena-modern/artifacts/gifcfzdk_DSC06849.webp" alt="8 indoor padel courts overview at Padelarena Wädenswil" className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-slate-100">
            <BookCTA url="https://bookpadelarena.ch" label={t.courtsPage.bookCta} />
            <Link to="/#pricing" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold bg-white text-slate-900 hover:bg-slate-100 border border-slate-200 transition-colors">
              {t.courtsPage.viewPricing}
            </Link>
          </div>

          {faqs.length > 0 && (
            <FaqSection title={t.courtsPage.faqTitle} faqs={faqs} />
          )}
        </div>
      </section>
    </PageShell>
  );
};

// Golf Page

export default CourtsPage;
